/**
 * Document Mapping
 *
 * Maps uploaded documents to checklist items they can satisfy.
 * One uploaded document can cover multiple checklist items.
 */

/**
 * Common document types that startups upload
 */
export type UploadedDocumentType =
  | 'pitch-deck'
  | 'financials-bundle'
  | 'legal-bundle'
  | 'cap-table'
  | 'team-overview'
  | 'team-bios-cvs'
  | 'product-demo'
  | 'technical-docs'
  | 'customer-data'
  | 'compliance-docs'
  | 'hr-bundle'
  | 'ip-portfolio'
  | 'contracts-bundle'
  | 'single-document';

export const UPLOADED_DOCUMENT_LABELS: Record<UploadedDocumentType, string> = {
  'pitch-deck': 'Pitch Deck',
  'financials-bundle': 'Financial Package',
  'legal-bundle': 'Legal Documents Bundle',
  'cap-table': 'Cap Table',
  'team-overview': 'Team Overview',
  'team-bios-cvs': 'Team Bios & CVs',
  'product-demo': 'Product Demo / Documentation',
  'technical-docs': 'Technical Documentation',
  'customer-data': 'Customer & Sales Data',
  'compliance-docs': 'Compliance Package',
  'hr-bundle': 'HR Documentation',
  'ip-portfolio': 'IP Portfolio',
  'contracts-bundle': 'Contracts Bundle',
  'single-document': 'Single Document',
};

/**
 * Mapping of uploaded document types to checklist item IDs they can satisfy
 */
export const DOCUMENT_COVERAGE_MAP: Record<UploadedDocumentType, string[]> = {
  'pitch-deck': [
    'com-1', // Pitch Deck
    'com-2', // Executive Summary
    'com-3', // Product Roadmap
    'com-4', // Competitive Analysis
    'com-8', // Pricing Strategy
    'com-9', // Go-to-Market Strategy
    'fin-6', // Financial Projections (often included)
    'fin-7', // Use of Funds
    'hr-1', // Founder Bios (team slide)
  ],
  'financials-bundle': [
    'fin-1', // Monthly Burn Rate & Runway
    'fin-2', // Bank Statements
    'fin-3', // P&L Statement
    'fin-4', // Balance Sheet
    'fin-5', // Cash Flow Statement
    'fin-6', // Financial Projections
    'fin-7', // Use of Funds
    'fin-8', // Audited Financials
    'fin-9', // Revenue Breakdown
    'fin-10', // Unit Economics
    'fin-11', // AR Aging
    'fin-12', // AP Aging
    'fin-13', // Debt Schedule
    'fin-14', // Related Party Transactions
    'fin-15', // Budget vs Actuals
    'fin-16', // Tax Returns
  ],
  'legal-bundle': [
    'corp-1', // Certificate of Incorporation
    'corp-2', // Bylaws
    'corp-3', // Shareholder Agreement
    'corp-5', // Board Resolutions
    'corp-6', // Stockholder Consents
    'corp-7', // Good Standing
    'corp-9', // Investor Rights Agreement
    'corp-10', // ROFR / Co-Sale
    'corp-11', // Voting Agreements
    'corp-12', // Stock Purchase Agreements
    'corp-13', // SAFE / Convertible Notes
    'corp-17', // Legal Opinion Letters
  ],
  'cap-table': [
    'corp-4', // Cap Table
    'corp-14', // 409A Valuation
    'corp-15', // Stock Option Plan
    'corp-16', // Option Grant Ledger
  ],
  'team-overview': [
    'hr-1', // Founder Bios
    'hr-2', // Org Chart
    'hr-3', // Key Employee List
    'hr-5', // Full Employee Roster
    'corp-8', // Legal Org Chart
  ],
  'team-bios-cvs': [
    'hr-1', // Founder Bios
    'hr-3', // Key Employee List
  ],
  'product-demo': [
    'tech-1', // Product Demo
    'com-3', // Product Roadmap
  ],
  'technical-docs': [
    'tech-2', // Technical Architecture
    'tech-3', // Technology Stack
    'tech-4', // System Architecture Diagrams
    'tech-5', // Data Flow Documentation
    'tech-6', // API Documentation
    'tech-7', // Development Methodology
    'tech-8', // QA / Testing
    'tech-9', // Uptime / SLA
    'tech-14', // Incident Response
    'tech-15', // Business Continuity / DR
    'tech-16', // Third-Party Dependencies
    'tech-17', // Technical Debt
    'tech-18', // Infrastructure Cost
  ],
  'customer-data': [
    'com-5', // Customer List
    'com-6', // Sales Pipeline
    'com-7', // Case Studies
    'com-10', // Top Customer Contracts
    'com-11', // Customer Concentration
    'com-12', // Churn Analysis
    'com-13', // Cohort Analysis
    'com-14', // NPS Scores
  ],
  'compliance-docs': [
    'tech-10', // Security Certifications
    'tech-11', // SOC 2
    'tech-12', // Pen Test Results
    'tech-13', // Data Privacy Compliance
    'comp-1', // Regulatory Licenses
    'comp-2', // Compliance Policies
    'comp-3', // Regulatory Correspondence
    'comp-4', // Pending Regulatory Matters
    'comp-5', // Environmental Permits
  ],
  'hr-bundle': [
    'hr-1', // Founder Bios
    'hr-2', // Org Chart
    'hr-3', // Key Employee List
    'hr-4', // Employment Agreement
    'hr-5', // Employee Roster
    'hr-6', // Offer Letters
    'hr-7', // Employee Handbook
    'hr-8', // Contractor Agreements
    'hr-9', // Non-Compete
    'hr-10', // Key Employee Retention
    'hr-11', // Commission Plans
    'hr-12', // Benefits Summary
    'hr-13', // HR Policies
    'hr-14', // D&I Data
    'hr-15', // Turnover Analysis
    'hr-16', // Employment Disputes
    'hr-17', // Immigration Status
    'corp-18', // D&O Questionnaires
  ],
  'ip-portfolio': [
    'ip-1', // IP Assignment
    'ip-2', // PIIA
    'ip-3', // IP Inventory
    'ip-4', // Patents
    'ip-5', // Trademarks
    'ip-6', // Copyrights
    'ip-7', // Open Source Audit
    'ip-8', // Inbound Licenses
    'ip-9', // Outbound Licenses
    'ip-10', // FTO Opinion
    'ip-11', // IP Litigation
    'ip-12', // Trade Secret Policies
  ],
  'contracts-bundle': [
    'cont-1', // Lease Agreements
    'cont-2', // Equipment Leases
    'cont-3', // Insurance Policies
    'cont-4', // Major Vendor Contracts
    'cont-5', // Loan Agreements
    'cont-6', // Guarantees
    'cont-7', // Material Contract Summary
    'cont-8', // Change of Control
    'com-15', // Partnership Agreements
    'com-16', // Channel Agreements
  ],
  'single-document': [],
};

/**
 * Get all checklist items covered by an uploaded document type
 */
export function getChecklistItemsCovered(docType: UploadedDocumentType): string[] {
  return DOCUMENT_COVERAGE_MAP[docType] || [];
}

/**
 * Suggest document bundle type based on filename
 */
export function suggestDocumentType(fileName: string): UploadedDocumentType {
  const lower = fileName.toLowerCase();

  if (lower.includes('pitch') || lower.includes('deck') || lower.includes('presentation')) {
    return 'pitch-deck';
  }
  if (lower.includes('financial') || lower.includes('p&l') || lower.includes('balance') || lower.includes('projection')) {
    return 'financials-bundle';
  }
  if (lower.includes('cap') && lower.includes('table')) {
    return 'cap-table';
  }
  if (lower.includes('legal') || lower.includes('incorporation') || lower.includes('bylaws') || lower.includes('agreement')) {
    return 'legal-bundle';
  }
  if (lower.includes('cv') || lower.includes('resume') || lower.includes('curriculum')) {
    return 'team-bios-cvs';
  }
  if (lower.includes('team') || lower.includes('org') || lower.includes('bio') || lower.includes('founder')) {
    return 'team-overview';
  }
  if (lower.includes('demo') || lower.includes('product')) {
    return 'product-demo';
  }
  if (lower.includes('technical') || lower.includes('architecture') || lower.includes('api')) {
    return 'technical-docs';
  }
  if (lower.includes('customer') || lower.includes('sales') || lower.includes('pipeline')) {
    return 'customer-data';
  }
  if (lower.includes('compliance') || lower.includes('soc') || lower.includes('security')) {
    return 'compliance-docs';
  }
  if (lower.includes('hr') || lower.includes('employee') || lower.includes('handbook')) {
    return 'hr-bundle';
  }
  if (lower.includes('ip') || lower.includes('patent') || lower.includes('trademark')) {
    return 'ip-portfolio';
  }
  if (lower.includes('contract') || lower.includes('lease') || lower.includes('insurance')) {
    return 'contracts-bundle';
  }

  return 'single-document';
}

/**
 * Upload record for a document that covers multiple items
 */
export interface UploadedDocument {
  id: string;
  fileName: string;
  fileUrl?: string;
  uploadedAt: string;
  documentType: UploadedDocumentType;
  coversItems: string[]; // Checklist item IDs this document covers
  notes?: string;
}
